import PricingPage from './_PricingPage';
import { Loader } from '../shared/Loader';

import { ApplicationState } from '../../types/state/storeTypes';
import { PricingPageWrapperProps, PricingPageWrapperStateProps } from '../../types/components/pricing/PricingPageWrapper';
import { PricingPageProps } from '../../types/components/pricing/PricingPage';
import { User } from '../../types/api/UsersTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { GenericResponse } from '../../types/api/Http';
import { PlanVariantResponse } from '../../types/api/SubscriptionTypes';

import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import axios from 'axios';

import { ENV } from '../../constants/environments';
const planVariantsUrl = `/plan_variants`;
const baseUrl = ENV.api.baseURL;

import { encodeLicenseId, decodeLicenseId } from '../../helpers/enterpriseLicenseHelpers';
import subscriptionUpgradeHelpers from '../../helpers/subscriptionUpgradeHelpers';

const PricingPageWrapper: React.FC<PricingPageWrapperProps> = ({
  pageContent,
  user,
  authenticated,
  subscription,
}) => {
  const [plans, setPlans] = useState<PlanVariantResponse[]>();
  const [pageProps, setPageProps] = useState<PricingPageProps>({ pageContent, planVariants: undefined });
  const router = useRouter();

  const { getMinimalLicenseIdForUpgrade } = subscriptionUpgradeHelpers;

  // Check if user should be redirected to enterprise pricing
  const shouldRedirectToEnterprise = () => {
    const minLicenseId = getMinimalLicenseIdForUpgrade(user, subscription, 'license_id');
    return minLicenseId > 2;
  };

  const fetchPlans = async () => {
    try {
      // Check if we need to redirect to enterprise page
      if (shouldRedirectToEnterprise() && !router.asPath.includes('/enterprise')) {
        const minLicenseId = getMinimalLicenseIdForUpgrade(user, subscription, 'license_id');
        const encodedId = encodeLicenseId(minLicenseId);
        router.push(`/pricing/enterprise?plan_code=${encodedId}`);
        return;
      }

      let url = `${baseUrl}${planVariantsUrl}`;

      // Handle both direct license_id parameter and encoded plan_code parameter
      const directLicenseId = router.query.license_id;
      const encodedPlanCode = router.query.plan_code;

      if (directLicenseId) {
        // Direct license_id has priority over encoded plan_code
        url = `${url}?license_id=${directLicenseId}`;
      } else if (encodedPlanCode) {
        // Decode the license ID from the encoded plan_code parameter
        const decodedLicenseId = decodeLicenseId(encodedPlanCode as string);
        if (decodedLicenseId) {
          url = `${url}?license_id=${decodedLicenseId}`;
        }
      }

      const result: GenericResponse<PlanVariantResponse[]> = await axios.get(url, {
        headers: { authorization: user?.token }
      });

      setPlans(result.data);
      const newPageProps = { ...pageProps, planVariants: result.data };
      setPageProps(newPageProps);
    }
    catch (e) {
      console.error('Error fetching plans:', e);
      router.push('/500');
    }
  };

  useEffect(() => {
    if (authenticated !== true && authenticated !== false) return;

    fetchPlans();
  }, [authenticated, router.query, user]);

  if (plans === undefined) return <Loader />;

  return <PricingPage {...pageProps} />;
};

const mapStateToProps = (state: ApplicationState): PricingPageWrapperStateProps => ({
  user: state?.auth?.user as User,
  authenticated: state?.auth?.authenticated,
  subscription: state?.auth?.subscription as SubscriptionDetail,
});

export default connect(mapStateToProps)(PricingPageWrapper);
